import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Row, Tabs, Timeline, Spin } from 'antd';
import { AudioOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';
import useApi from '../../hooks/useApi';
import Datestamp from 'vl-common/src/components/Datestamp';

interface Recording {
  appt_id: number;
  call_id: number;
  call_type_code: 'AUDIO' | 'VIDEO';
  ext_call_id: string;
  call_start_date: Date;
  recording_state_code?: 'COMPSTART' | 'COMPEND' | 'COMPERROR';
  recording_state_desc?: string;
  patient_user_guid: string;
  clinician_user_guid: string;
  isCompleted: boolean;
  isCancelled?: boolean;
}

const RecordingPreview = ({ recording }: { recording: Recording }) => {
  const { t } = useTranslation('common');
  const { Api } = useApi();
  const { data: audioSrc } = useQuery({
    queryKey: ['mediaFile', recording?.ext_call_id],
    queryFn: async () => {
      return Api.admin.AdminUsers.GetFile(
        recording?.patient_user_guid,
        recording?.call_type_code === 'AUDIO'
          ? `${recording?.ext_call_id}.mp3`
          : `${recording?.clinician_user_guid}-${recording?.appt_id}.mp4`,
        true
      ).then((res) => {
        return res?.data?.url || '';
      });
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
  if (audioSrc === '') return t('Unable to locate audio file');
  if (!audioSrc) return <Spin />;
  if (recording?.call_type_code === 'AUDIO') return <audio controls src={audioSrc} controlsList="nodownload" />;
  if (recording?.call_type_code === 'VIDEO')
    return <video controls src={audioSrc} controlsList="nodownload" width={300} height={200} />;
  return t('An error occured. Unable to display media.');
};

const RecordingsHandler = ({ recording }: { recording: Recording }) => {
  const { t } = useTranslation('common');
  const { Api } = useApi();

  const requestRecording = useMutation({
    mutationFn: ({ call_id }: { call_id: number }) =>
      Api.clinicians.requestCallRecording({ call_id, appt_id: recording?.appt_id, recording_state: 'COMPSTART' })
  });

  if (!recording?.isCompleted && !recording?.isCancelled) {
    return t('Recordings cannot be requested before the appointment is completed or cancelled');
  }

  if (recording.recording_state_code === 'COMPSTART' || recording.recording_state_code === 'COMPERROR') {
    return recording.recording_state_desc ?? null;
  }
  if (recording.recording_state_code === 'COMPEND') {
    return <RecordingPreview recording={recording} />;
  }
  if (requestRecording?.isSuccess) return t('Recording Requested');
  return (
    <Button
      disabled={requestRecording.isSuccess}
      onClick={() => requestRecording.mutate({ call_id: recording?.call_id })}
      type="primary"
      loading={requestRecording.isPending}
      className="request-button"
      data-testid="request-recording-button"
    >
      {t('Request recording')}
    </Button>
  );
};

const RecordingsContainer = ({
  case_id,
  appointments
}: {
  case_id?: number;
  appointments: { appt_status_code: string; appt_id: number }[];
}) => {
  const { t } = useTranslation('common');
  const { Api } = useApi();

  const { data: rawRecordings } = useQuery({
    queryKey: ['recordingsAvailability', case_id],
    queryFn: () => {
      return case_id
        ? Api.clinicians.getRecordingAvailability({ case_id }).then((response) => {
            return (
              response?.data?.appt_calls?.sort((a: Recording, b: Recording) => (a.call_id > b.call_id ? 1 : -1)) || []
            );
          })
        : [];
    }
  });

  const data = useMemo(() => {
    if (!rawRecordings) return undefined;

    return rawRecordings.map((call: Recording) => {
      const appointment_data = appointments?.find((appt) => appt?.appt_id === call?.appt_id);
      const isCompleted = appointment_data?.appt_status_code === 'COMPLETE';
      const isCancelled = appointment_data?.appt_status_code === 'CANCELLED';
      return { ...call, isCompleted, isCancelled };
    });
  }, [rawRecordings, appointments]);

  if (!data)
    return (
      <div
        style={{
          margin: '1em',
          padding: '1em 0',
          border: '1px solid rgba(0, 0, 0, 0.06)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {t('Loading..')}
      </div>
    );

  const groupedData = Object.groupBy(data, ({ appt_id }: { appt_id: number }) => appt_id) as Record<
    string,
    Recording[]
  >;

  return (
    <div style={{ margin: '1em', padding: '1em 0', border: '1px solid rgba(0, 0, 0, 0.06)' }}>
      <Tabs
        defaultActiveKey={Object.keys(groupedData)?.[0] || '1'}
        tabPosition="left"
        items={Object.keys(groupedData)?.map((appt_id: string) => ({
          key: appt_id,
          label: `Appointment ${appt_id}`,
          children: (
            <Timeline>
              {groupedData?.[appt_id]?.map((recording: Recording) => (
                <Timeline.Item
                  key={`recording-${recording?.call_id}`}
                  dot={recording?.call_type_code === 'AUDIO' ? <AudioOutlined /> : <VideoCameraOutlined />}
                >
                  <Row style={{ paddingBottom: 10 }}>
                    <div style={{ marginRight: 10 }}>
                      {recording?.call_type_code === 'AUDIO' ? 'Audio Call' : 'Video Call'}
                    </div>
                    (<Datestamp date={recording?.call_start_date} format="Date @ Time - 03/01/2022 @ 08:57" />)
                  </Row>
                  <Row>
                    <RecordingsHandler recording={recording} />
                  </Row>
                </Timeline.Item>
              ))}
            </Timeline>
          )
        }))}
      />
    </div>
  );
};

export { RecordingsContainer };
